import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { User, Mail, Calendar, Trophy, Target, Flame, Clock, Check, ArrowLeft, Pencil, Save, X, Star, Smartphone, Trash2, Download, RotateCcw, KeyRound, Copy, CheckCircle2, Loader2, AlertTriangle, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachPlayerDetail() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ birth_year: '', current_team: '' });
  const [saving, setSaving] = useState(false);
  const [scoredOnly, setScoredOnly] = useState(false);
  const [resetLink, setResetLink] = useState(null); // { url, expires_at, email }
  const [generatingReset, setGeneratingReset] = useState(false);
  const [copiedReset, setCopiedReset] = useState(false);

  const handleGenerateResetLink = async () => {
    setGeneratingReset(true);
    try {
      const res = await adminAPI.createPasswordReset(playerId);
      setResetLink(res.data);
      setCopiedReset(false);
      // Auto-copy on generate for convenience
      try {
        await navigator.clipboard.writeText(res.data.url);
        setCopiedReset(true);
        toast.success('Reset link generated and copied to clipboard');
      } catch {
        toast.success('Reset link generated — tap Copy below');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate reset link');
    }
    setGeneratingReset(false);
  };

  const handleCopyResetLink = async () => {
    if (!resetLink?.url) return;
    try {
      await navigator.clipboard.writeText(resetLink.url);
      setCopiedReset(true);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — long-press the link to copy manually');
    }
  };

  useEffect(() => { load(); }, [playerId]);

  const load = async () => {
    try {
      const res = await adminAPI.playerDetail(playerId);
      setPlayer(res.data);
      const p = res.data.profile || {};
      setEditForm({ birth_year: p.birth_year || '', current_team: p.current_team || '' });
    } catch {
      toast.error('Failed to load player');
      navigate('/coach/players');
    }
    setLoading(false);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const data = {};
      if (editForm.birth_year) data.birth_year = parseInt(editForm.birth_year);
      if (editForm.current_team) data.current_team = editForm.current_team;
      await adminAPI.updatePlayerProfile(playerId, data);
      toast.success('Profile updated');
      setEditing(false);
      await load();
    } catch {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  const handleExportCSV = () => {
    const rows = player?.recent_progress || [];
    if (!rows.length) { toast.error('No activity to export'); return; }
    const header = ['Drill', 'Category', 'Week', 'Day', 'Date', 'Time', 'Score (out of 30)', 'Effort (out of 10)', 'Late'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(',')];
    rows.forEach(p => {
      const ts = p.completed_at ? new Date(p.completed_at) : null;
      lines.push([
        escape(p.drill_title || 'Unknown drill'),
        escape(p.drill_category || ''),
        p.week_number ?? '',
        p.day_number ?? '',
        ts ? ts.toLocaleDateString('en-US') : '',
        ts ? ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
        p.score?.total ?? '',
        p.score?.effort_rating ?? p.effort_rating ?? '',
        p.is_late ? 'Yes' : 'No',
      ].join(','));
    });
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const safeName = (player.name || 'player').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `${safeName}_activity_${new Date().toISOString().slice(0, 10)}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const handleReset = async () => {
    const ok = window.confirm(
      `RESET ${player?.name?.toUpperCase() || 'this player'} to Week 1?\n\nThis will permanently delete:\n• All drill progress\n• Streak & badges\n• Feedback submissions\n• Weekly Challenge submissions\n\nProfile, devices, and account stay intact. Cannot be undone.`
    );
    if (!ok) return;
    try {
      const res = await adminAPI.resetPlayer(playerId);
      toast.success(res.data?.message || 'Player reset');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  if (loading) return (
    <Layout title="PLAYER" showBack>
      <div className="flex justify-center py-20"><div className="spinner" /></div>
    </Layout>
  );

  if (!player) return null;

  const profile = player.profile || {};
  const streak = player.streak || {};
  const stats = player.stats || {};

  return (
    <Layout title="PLAYER DETAIL" showBack>
      <div className="px-4 py-6 space-y-4">
        <button
          onClick={() => navigate('/coach/players')}
          className="flex items-center gap-1 text-[#64748B] text-xs hover:text-[#0A2242] transition-colors mb-2"
          data-testid="back-to-players"
        >
          <ArrowLeft size={14} /> Back to Players
        </button>

        {/* Player Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-5"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#E2E8F0] flex items-center justify-center">
              <User size={24} className="text-[#64748B]" />
            </div>
            <div className="flex-1">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold" data-testid="player-name">{player.name}</h2>
              <div className="flex items-center gap-1 mt-1 text-[#64748B]">
                <Mail size={12} />
                <span className="text-xs" data-testid="player-email">{player.email}</span>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                  player.subscription_active ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-red-500/15 text-red-400'
                }`} data-testid="player-status">
                  {player.subscription_active ? 'Active' : 'Inactive'}
                </span>
                {player.age_group && (
                  <span className="text-[10px] font-bold text-[#A78BFA] bg-[#A78BFA]/10 px-2 py-0.5 rounded-full" data-testid="player-age-group">
                    {player.age_group}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Info */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]">Profile</h3>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                data-testid="edit-profile-btn"
              >
                <Pencil size={10} /> Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#10B981] hover:text-[#059669] transition-colors"
                  data-testid="save-profile-btn"
                >
                  <Save size={10} /> {saving ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditing(false); const p = player.profile || {}; setEditForm({ birth_year: p.birth_year || '', current_team: p.current_team || '' }); }}
                  className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-400 hover:text-red-600 transition-colors"
                  data-testid="cancel-edit-btn"
                >
                  <X size={10} /> Cancel
                </button>
              </div>
            )}
          </div>
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold block mb-1">Birth Year</label>
                <input
                  type="number"
                  value={editForm.birth_year}
                  onChange={e => setEditForm(f => ({ ...f, birth_year: e.target.value }))}
                  placeholder="e.g. 2010"
                  className="w-full p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none"
                  data-testid="edit-birth-year"
                />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold block mb-1">Current Team</label>
                <input
                  type="text"
                  value={editForm.current_team}
                  onChange={e => setEditForm(f => ({ ...f, current_team: e.target.value }))}
                  placeholder="e.g. Central Catholic"
                  className="w-full p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none"
                  data-testid="edit-current-team"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InfoCard icon={<Target size={14} />} label="Position" value={profile.position || '—'} />
                <InfoCard icon={<Trophy size={14} />} label="Skill Level" value={profile.skill_level || '—'} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <InfoCard icon={<Target size={14} />} label="Position" value={profile.position || '—'} />
              <InfoCard icon={<Trophy size={14} />} label="Skill Level" value={profile.skill_level || '—'} />
              <InfoCard icon={<Calendar size={14} />} label="Birth Year" value={profile.birth_year || '—'} />
              <InfoCard icon={<User size={14} />} label="Team" value={profile.current_team || '—'} />
              {profile.phone && (
                <InfoCard icon={<User size={14} />} label="Phone" value={`${profile.phone}${profile.sms_opt_in ? ' · SMS ✓' : ''}`} />
              )}
              {profile.referral_source && (
                <div className="col-span-2 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-lg px-3 py-2.5">
                  <p className="text-[9px] uppercase tracking-widest text-[#0891B2] font-bold">Referred By</p>
                  <p className="text-sm font-bold text-[#0A2242] mt-0.5">{profile.referral_source}</p>
                  {profile.referral_detail && <p className="text-[11px] text-[#64748B] mt-0.5 italic">"{profile.referral_detail}"</p>}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]">Training Stats</h3>
            <button
              onClick={async () => {
                const input = window.prompt(
                  `Set ${player.name}'s current streak.\n\nCurrent: ${streak.current || 0}${streak.natural !== undefined ? ` (natural: ${streak.natural}, bonus: +${streak.bonus || 0})` : ''}\nBest: ${streak.longest || 0}\n\nThis is STICKY — it survives auto-recomputes by storing the difference as a bonus on top of the player's natural plan streak.\n\nEnter the new total streak:`,
                  String(streak.current || 0)
                );
                if (input === null) return;
                const n = parseInt(input, 10);
                if (!Number.isFinite(n) || n < 0 || n > 365) {
                  toast.error('Enter a number between 0 and 365');
                  return;
                }
                try {
                  const res = await adminAPI.setPlayerStreak(playerId, n, null);
                  toast.success(res.data?.message || 'Streak updated');
                  await load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to update streak');
                }
              }}
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#FF9100] hover:text-[#e07e00] transition-colors"
              data-testid="edit-streak-btn"
            >
              <Flame size={11} /> Edit Streak
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatBox value={stats.total_drills_completed} label="Drills Done" color="text-[#2563EB]" />
            <StatBox value={streak.current || 0} label="Overall Streak" color="text-[#FF9100]" icon={<Flame size={14} className="text-[#FF9100]" />} />
            <StatBox value={streak.longest || 0} label="Best Streak" color="text-[#A78BFA]" />
            <StatBox value={player.current_week || 1} label="Current Week" color="text-[#00E5FF]" />
          </div>

          {/* Weekly Streak card — resets every Monday, drives the score bonus */}
          <div
            className="mt-3 bg-gradient-to-r from-[#FF9100]/10 to-[#FACC15]/10 border border-[#FF9100]/30 rounded-xl p-4"
            data-testid="weekly-streak-card"
          >
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-center gap-2">
                <Flame size={14} className="text-[#FF9100]" />
                <span className="text-[10px] font-black uppercase tracking-widest text-[#64748B]">Weekly Streak</span>
                <button
                  onClick={async () => {
                    const raw = window.prompt(
                      `Set this week's streak for ${player.name} (0–5 days):`,
                      String(streak.weekly ?? 0)
                    );
                    if (raw == null) return;
                    const n = parseInt(raw, 10);
                    if (Number.isNaN(n) || n < 0 || n > 5) {
                      toast.error('Enter 0 through 5');
                      return;
                    }
                    try {
                      const res = await adminAPI.setPlayerWeeklyStreak(playerId, n);
                      toast.success(res.data?.message || 'Weekly streak updated');
                      await load();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to update weekly streak');
                    }
                  }}
                  className="ml-1 text-[9px] font-bold uppercase tracking-wider text-[#FF9100] hover:text-[#e07e00] underline-offset-2 hover:underline transition-colors"
                  data-testid="edit-weekly-streak-btn"
                >
                  Edit
                </button>
              </div>
              <div>
                <span className="font-['Barlow_Condensed'] text-2xl font-black text-[#FF9100] leading-none">{streak.weekly || 0}</span>
                <span className="text-xs text-[#64748B] font-semibold ml-1">/ 5</span>
                {stats.weekly_streak_bonus > 0 && (
                  <span className="ml-2 text-xs text-[#10B981] font-black">+{stats.weekly_streak_bonus} pts</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((d) => (
                <div
                  key={d}
                  className={`flex-1 h-2 rounded-full ${
                    (streak.weekly || 0) >= d ? 'bg-gradient-to-r from-[#FF9100] to-[#FACC15]' : 'bg-[#E2E8F0]'
                  }`}
                />
              ))}
            </div>
            <p className="text-[10px] text-[#64748B] mt-2 leading-snug">
              Mon&ndash;Fri scheduled days completed this week. Resets every Monday. Each day adds <span className="font-bold text-[#FF9100]">+2 pts</span> to Overall Score (max <span className="font-bold text-[#FF9100]">+10</span>).
            </p>
          </div>

          {/* Overall Score — lifetime % + weekly streak bonus (same metric as Top Scores leaderboard) */}
          <div
            className="mt-3 bg-gradient-to-r from-[#10B981]/15 to-[#2563EB]/15 border border-[#10B981]/40 rounded-xl p-4"
            data-testid="overall-score-banner"
          >
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-center gap-2">
                <Star size={14} className="text-[#10B981]" />
                <span className="text-[10px] font-black uppercase tracking-widest text-[#64748B]">Overall Score</span>
              </div>
              <div className="text-right">
                <span className="font-['Barlow_Condensed'] text-3xl font-black text-[#10B981] leading-none" data-testid="overall-score-value">
                  {stats.weekly_avg_score || 0}
                </span>
                <span className="text-sm text-[#64748B] font-semibold ml-0.5">%</span>
              </div>
            </div>
            <div className="h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#10B981] to-[#00E5FF] rounded-full transition-all"
                style={{ width: `${Math.min(100, Math.max(0, stats.weekly_avg_score || 0))}%` }}
              />
            </div>
            {stats.weekly_streak_bonus > 0 ? (
              <p className="text-[10px] text-[#64748B] mt-2 leading-snug">
                <span className="font-bold text-[#0A2242]">{stats.base_score}%</span> base
                <span className="text-[#FF9100] font-bold"> +{stats.weekly_streak_bonus}</span> weekly streak bonus.
                Matches the <span className="font-semibold text-[#0A2242]">Top Scores</span> leaderboard ranking.
              </p>
            ) : (
              <p className="text-[10px] text-[#64748B] mt-2 leading-snug">
                Lifetime average across every scored training day. Weekly streak adds bonus points (currently <span className="font-bold">+0</span>). Matches the <span className="font-semibold text-[#0A2242]">Top Scores</span> leaderboard ranking.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 mt-3">
            <StatBox value={stats.feedback_submitted} label="Videos Submitted" color="text-[#A78BFA]" />
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3">Timeline</h3>
          <div className="space-y-2">
            <TimelineItem icon={<Calendar size={12} />} label="Joined" value={player.created_at ? new Date(player.created_at).toLocaleDateString() : '—'} />
            <TimelineItem icon={<Clock size={12} />} label="Training Started" value={player.training_start_date || '—'} />
            <TimelineItem icon={<Check size={12} />} label="Onboarding" value={player.onboarding_complete ? 'Complete' : 'Incomplete'} />
          </div>
        </motion.div>

        {/* Activity Log */}
        {player.recent_progress?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
            data-testid="activity-log-section"
          >
            {(() => {
              const all = player.recent_progress;
              const scoredItems = all.filter(p => p.score?.total != null);
              const displayItems = scoredOnly ? scoredItems : all;
              const totalScore = scoredItems.reduce((s, p) => s + (p.score?.total || 0), 0);
              const avgScore = scoredItems.length ? Math.round((totalScore / scoredItems.length) * 10) / 10 : 0;
              return (
                <>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]">
                      Activity Log <span className="text-[#94A3B8] font-normal normal-case tracking-normal">({displayItems.length} {scoredOnly ? 'scored' : 'total'})</span>
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setScoredOnly(v => !v)}
                        className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
                          scoredOnly
                            ? 'bg-[#2563EB] border-[#2563EB] text-white'
                            : 'bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]'
                        }`}
                        data-testid="toggle-scored-only"
                      >
                        <Star size={11} /> Scores Only
                      </button>
                      <button
                        onClick={handleExportCSV}
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                        data-testid="export-activity-csv"
                      >
                        <Download size={11} /> CSV
                      </button>
                    </div>
                  </div>

                  {/* Score summary */}
                  {scoredItems.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3" data-testid="score-summary">
                      <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg px-2 py-1.5 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-widest text-[#64748B]">Scored</div>
                        <div className="font-['Barlow_Condensed'] text-base font-black text-[#10B981] leading-none mt-0.5">{scoredItems.length}</div>
                      </div>
                      <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-lg px-2 py-1.5 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-widest text-[#64748B]">Avg Score</div>
                        <div className="font-['Barlow_Condensed'] text-base font-black text-[#2563EB] leading-none mt-0.5">{avgScore}<span className="text-[10px] text-[#64748B] font-normal">/30</span></div>
                      </div>
                      <div className="bg-[#FF9100]/10 border border-[#FF9100]/30 rounded-lg px-2 py-1.5 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-widest text-[#64748B]">Best</div>
                        <div className="font-['Barlow_Condensed'] text-base font-black text-[#FF9100] leading-none mt-0.5">{scoredItems.reduce((m, p) => Math.max(m, p.score?.total || 0), 0)}<span className="text-[10px] text-[#64748B] font-normal">/30</span></div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
                    {displayItems.map((p, i) => {
                      const ts = p.completed_at ? new Date(p.completed_at) : null;
                      const dateStr = ts ? ts.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';
                      const timeStr = ts ? ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
                      const score = p.score?.total;
                      const effort = p.score?.effort_rating ?? p.effort_rating;
                      return (
                        <div
                          key={i}
                          className="flex items-start gap-3 p-2.5 rounded-lg bg-[#F8FAFC] border border-transparent hover:border-[#E2E8F0] transition-colors"
                          data-testid={`activity-row-${i}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                            <Check size={12} className="text-[#10B981]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <p className="text-xs font-bold text-[#0A2242] truncate" title={p.drill_title}>{p.drill_title || 'Drill'}</p>
                              <span className="text-[9px] uppercase tracking-widest text-[#FF9100] font-bold flex-shrink-0">W{p.week_number} · D{p.day_number}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#64748B]">
                              <Calendar size={10} />
                              <span>{dateStr}</span>
                              {timeStr && (
                                <>
                                  <Clock size={10} />
                                  <span>{timeStr}</span>
                                </>
                              )}
                              {p.is_late && <span className="text-amber-500 font-bold uppercase tracking-wider text-[8px]">Late</span>}
                            </div>
                            {(score != null || effort != null) && (
                              <div className="flex items-center gap-3 mt-1 text-[10px]">
                                {score != null && (
                                  <span className="flex items-center gap-1 text-[#10B981] font-bold">
                                    <Star size={10} /> {score}/30
                                  </span>
                                )}
                                {effort != null && (
                                  <span className="text-[#A78BFA] font-bold">Effort {effort}/10</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {displayItems.length === 0 && (
                      <div className="text-center py-6 text-[#94A3B8] text-xs">
                        No scored drills yet.
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </motion.div>
        )}

        {/* Recent Logins — surface suspicious sharing patterns */}
        {player.login_history?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className={`border rounded-xl p-4 ${player.login_suspicion?.flag ? 'bg-[#FEF2F2] border-[#FCA5A5]' : 'bg-white border-[#E2E8F0]'}`}
            data-testid="recent-logins-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B] flex items-center gap-1.5">
                <Activity size={12} /> Recent Logins ({player.login_history.length})
              </h3>
              {player.login_suspicion?.flag && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-[#DC2626] bg-white border border-[#FCA5A5] px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="suspicion-badge">
                  <AlertTriangle size={10} /> Possible Sharing
                </span>
              )}
            </div>
            {player.login_suspicion?.flag && player.login_suspicion?.reasons?.length > 0 && (
              <ul className="text-[10px] text-[#991B1B] mb-3 space-y-0.5 pl-3" data-testid="suspicion-reasons">
                {player.login_suspicion.reasons.map((r, i) => (
                  <li key={i} className="list-disc">{r}</li>
                ))}
              </ul>
            )}
            <div className="space-y-1.5" data-testid="login-history-list">
              {player.login_history.slice(0, 5).map((e, i) => {
                const ts = e.at ? new Date(e.at) : null;
                return (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white/70" data-testid={`login-entry-${i}`}>
                    <Clock size={12} className="text-[#94A3B8] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-[#0A2242]">{e.device_name || 'Unknown device'}</p>
                      <p className="text-[9px] text-[#94A3B8]">
                        {ts ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}
                      </p>
                    </div>
                    <code className="text-[8px] text-[#94A3B8] font-mono shrink-0">{(e.device_id || '').slice(-8)}</code>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Registered Devices (Coach-only management) */}
        {player.registered_devices?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          >
            <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-1.5">
              <Smartphone size={12} /> Registered Devices ({player.registered_devices.length})
            </h3>
            <div className="space-y-2">
              {player.registered_devices.map(d => (
                <div key={d.device_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F8FAFC]" data-testid={`admin-device-${d.device_id}`}>
                  <Smartphone size={14} className="text-[#94A3B8]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[#0A2242]">{d.device_name}</p>
                    <p className="text-[9px] text-[#94A3B8]">
                      Last: {d.last_login ? new Date(d.last_login).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Remove "${d.device_name}" from ${player.name}'s account?`)) return;
                      try {
                        await adminAPI.removePlayerDevice(playerId, d.device_id);
                        toast.success('Device removed');
                        load();
                      } catch { toast.error('Failed to remove'); }
                    }}
                    className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                    data-testid={`admin-remove-device-${d.device_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
        {/* Account Tools */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          data-testid="account-tools-card"
        >
          <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">Account Tools</h3>
          <p className="text-[11px] text-[#64748B] leading-relaxed mb-3">
            Generate a one-time password reset link to share with this player via text or call. Link expires in <span className="font-semibold text-[#0A2242]">1 hour</span> and works only once.
          </p>
          {!resetLink ? (
            <button
              onClick={handleGenerateResetLink}
              disabled={generatingReset}
              className="w-full flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-50 text-white font-bold uppercase tracking-wide rounded-lg px-4 py-2.5 text-xs transition-colors"
              data-testid="generate-reset-link-btn"
            >
              {generatingReset ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
              {generatingReset ? 'Generating…' : 'Generate Reset Link'}
            </button>
          ) : (
            <div data-testid="reset-link-result">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[10px] font-mono text-[#0A2242] break-all select-all mb-2">
                {resetLink.url}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyResetLink}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-bold uppercase tracking-wide rounded-lg px-3 py-2 text-xs transition-colors"
                  data-testid="copy-reset-link-btn"
                >
                  {copiedReset ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                  {copiedReset ? 'Copied' : 'Copy Link'}
                </button>
                <button
                  onClick={() => { setResetLink(null); setCopiedReset(false); }}
                  className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#64748B] hover:text-[#0A2242] transition-colors"
                  data-testid="dismiss-reset-link-btn"
                >
                  Done
                </button>
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-2 leading-snug">
                Send this link to <span className="font-semibold text-[#0A2242]">{resetLink.email}</span>. They open it on any device, set a new password, and can sign in immediately. Generating a new link invalidates this one.
              </p>
            </div>
          )}
        </motion.div>

        {/* Danger Zone */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-red-200 rounded-xl p-4"
        >
          <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-red-500 mb-2">Danger Zone</h3>
          <p className="text-[11px] text-[#64748B] leading-relaxed mb-3">
            Resets this player back to Week 1: clears all drill progress, streak, badges, feedback and challenge submissions. Profile and devices stay. Cannot be undone.
          </p>
          <button
            onClick={handleReset}
            className="w-full flex items-center justify-center gap-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 font-bold uppercase tracking-wide rounded-lg px-4 py-2.5 text-xs transition-colors"
            data-testid="reset-player-btn"
          >
            <RotateCcw size={14} /> Reset Player to Week 1
          </button>
        </motion.div>
      </div>
    </Layout>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="bg-[#F8FAFC] rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[#64748B] mb-1">
        {icon}
        <span className="text-[9px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <p className="text-sm font-bold text-[#0A2242]">{value}</p>
    </div>
  );
}

function StatBox({ value, label, color, icon }) {
  return (
    <div className="bg-[#F8FAFC] rounded-lg p-3 text-center">
      <div className="flex items-center justify-center gap-1">
        {icon}
        <span className={`font-['Barlow_Condensed'] text-2xl font-bold ${color}`}>{value}</span>
      </div>
      <p className="text-[8px] uppercase tracking-widest text-[#64748B] mt-1">{label}</p>
    </div>
  );
}

function TimelineItem({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="text-[#64748B]">{icon}</div>
      <span className="text-[#64748B] w-24">{label}</span>
      <span className="text-[#0A2242] font-bold">{value}</span>
    </div>
  );
}
